import { useMemo, useRef } from 'react';
import { useInfiniteQuery, InfiniteData } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { useAccountsService } from '@restworld/data-services';
import { QUERY_KEYS } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';
import { IndexEmployerUserContactResponse } from '@restworld/data-services';
import { EmployerUserContact } from '@restworld/utility-types';

const DEFAULT_OFFSET = 0;
const DEFAULT_LIMIT = 10;

export function useEmployerUserContactsListQuery(params?: { limit?: number; searchVector?: string }) {
  const accountsService = useAccountsService();
  const { user } = useAuth();
  const { limit = DEFAULT_LIMIT, searchVector } = params || {};
  const nextPageRef = useRef<number>(undefined);
  const hasNextPageRef = useRef<boolean>(undefined);
  const organizationId = user?.organization_id;
  const queryKey = useEmployerUserContactsListQueryKey();
  const currentQueryKey = searchVector ? [...queryKey, searchVector] : queryKey;
  const isRwMember = user?.type === 'rw_member';

  const query = useInfiniteQuery({
    queryKey: currentQueryKey,
    queryFn: ({ pageParam }) => {
      nextPageRef.current = pageParam + 1;
      return accountsService.listEmployerUserContacts({
        limit,
        offset: pageParam * limit,
        organizationId,
        searchVector,
      });
    },
    getNextPageParam: (lastPage) => {
      const totalHits = lastPage.data.hits;
      const employerContactsLoadedSoFar = nextPageRef.current * limit;

      hasNextPageRef.current = employerContactsLoadedSoFar < totalHits;
      return nextPageRef.current;
    },
    refetchOnWindowFocus: false,
    initialPageParam: DEFAULT_OFFSET,
    enabled: !!organizationId || isRwMember,
  });

  const employerUserContactsInfo = useMemo(() => extractEmployerUserContactInfo(query.data), [query.data]);

  return {
    employerUserContacts: employerUserContactsInfo.items,
    hits: employerUserContactsInfo.hits,
    hasNextPage: hasNextPageRef.current,
    isLoading: query.isLoading,
    query,
  };
}
export default useEmployerUserContactsListQuery;

type EmployerUserContactInfo = {
  items: EmployerUserContact[];
  hits: number;
};

export function extractEmployerUserContactInfo(
  data?: InfiniteData<AxiosResponse<IndexEmployerUserContactResponse, unknown>>
): EmployerUserContactInfo {
  if (!data || !data.pages) return { items: [], hits: 0 };
  const items = data?.pages.flatMap((page) => page.data.data);
  const hits = data?.pages[0].data.hits;

  return { items, hits };
}

export function useEmployerUserContactsListQueryKey() {
  const { user } = useAuth();
  return [QUERY_KEYS.ACCOUNTS.LIST_USER_CONTACTS, user?.organization_id];
}
